import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { format } from 'date-fns';
import { Loader2, Calendar, Star, AlertTriangle, Crown, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Single Day Card Component
function DayCard({ day, type, isLocked = false }) {
    const bgColors = {
        auspicious: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
        inauspicious: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800',
        nobleman: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    };

    const iconColors = {
        auspicious: 'text-emerald-600 dark:text-emerald-400',
        inauspicious: 'text-rose-600 dark:text-rose-400',
        nobleman: 'text-amber-600 dark:text-amber-400'
    };

    if (isLocked) {
        return (
            <div className={`p-3 rounded-lg border ${bgColors[type]} transition-all relative overflow-hidden`} data-testid={`day-card-locked-${day.day}`}>
                {/* Blurred content */}
                <div className="filter blur-sm pointer-events-none select-none">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-lg font-bold">{format(new Date(day.date), 'MMM d')}</div>
                            <div className="text-sm text-muted-foreground">{day.day_animal}</div>
                        </div>
                        <Badge variant="outline" className={iconColors[type]}>
                            {day.net_score > 0 ? '+' : ''}{day.net_score}
                        </Badge>
                    </div>
                </div>
                {/* Lock overlay */}
                <div className="absolute inset-0 bg-slate-100/60 dark:bg-slate-800/60 flex items-center justify-center">
                    <div className="flex items-center gap-2 bg-white dark:bg-slate-900 px-3 py-1.5 rounded-full shadow-md">
                        <Lock className="w-4 h-4 text-slate-500" />
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">PRO</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`p-3 rounded-lg border ${bgColors[type]} transition-all hover:shadow-md`} data-testid={`day-card-${day.day}`}>
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-lg font-bold">{format(new Date(day.date), 'MMM d')}</div>
                    <div className="text-sm text-muted-foreground">{day.day_animal}</div>
                </div>
                <Badge variant="outline" className={iconColors[type]}>
                    {day.net_score > 0 ? '+' : ''}{day.net_score}
                </Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
                {day.is_yellow_path && <Badge variant="secondary" className="text-xs">Yellow Path</Badge>}
                {day.is_nobleman_day && <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">貴人</Badge>}
                {day.is_clash_day && <Badge variant="destructive" className="text-xs">Clash</Badge>}
            </div>
            {day.factors && day.factors.length > 0 && (
                <div className="mt-2 space-y-1">
                    {day.factors.slice(0, 3).map((f, i) => (
                        <div key={i} className={`text-xs ${f.type === 'auspicious' ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {f.name}: {f.value}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Locked Month Overlay Component
function LockedMonthOverlay() {
    return (
        <div className="absolute inset-0 bg-slate-100/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
            <div className="text-center p-6">
                <Lock className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <h3 className="font-semibold text-lg text-slate-700 dark:text-slate-300">PRO Access Required</h3>
                <p className="text-sm text-slate-500 mt-1">Upgrade to view analysis for April onwards</p>
            </div>
        </div>
    );
}

// Main Component
function PersonalAnalysis() {
    const { user, token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [analysisData, setAnalysisData] = useState(null);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [hasProAccess, setHasProAccess] = useState(false);

    // Max visible dates for non-PRO users
    const FREE_VISIBLE_DATES = 2;
    // Max month for non-PRO users (March = 3)
    const FREE_MAX_MONTH = 3;

    // Check if user has PRO access
    useEffect(() => {
        const checkProAccess = async () => {
            if (!token) return;
            try {
                const response = await axios.get(`${API_URL}/api/auth/admin/check`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setHasProAccess(response.data.has_pro_access);
            } catch (error) {
                setHasProAccess(false);
            }
        };
        checkProAccess();
    }, [token]);

    // Check if current month is locked for non-PRO users
    const isMonthLocked = !hasProAccess && (selectedYear > 2026 || (selectedYear === 2026 && selectedMonth > FREE_MAX_MONTH));

    useEffect(() => {
        const fetchAnalysis = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const response = await axios.get(
                    `${API_URL}/api/tong-shu/personal-analysis/${selectedYear}/${selectedMonth}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setAnalysisData(response.data);
            } catch (error) {
                console.error('Error fetching analysis:', error);
                if (error.response?.status === 400) {
                    toast.error('Please set your birth year in Settings first');
                } else {
                    toast.error('Failed to load analysis');
                }
            }
            setLoading(false);
        };

        fetchAnalysis();
    }, [token, selectedYear, selectedMonth]);

    const handlePrevMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(prev => prev - 1);
        } else {
            setSelectedMonth(prev => prev - 1);
        }
    };

    const handleNextMonth = () => {
        // For non-PRO users, limit to March 2026
        if (!hasProAccess) {
            const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
            const nextYear = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
            // Allow navigation but show locked state
            if (nextYear > 2026 || (nextYear === 2026 && nextMonth > FREE_MAX_MONTH + 1)) {
                // Limit to April (show locked state)
                if (selectedMonth < 4 || selectedYear < 2026) {
                    setSelectedMonth(prev => prev === 12 ? 1 : prev + 1);
                    if (selectedMonth === 12) setSelectedYear(prev => prev + 1);
                }
                return;
            }
        }
        
        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(prev => prev + 1);
        } else {
            setSelectedMonth(prev => prev + 1);
        }
    };

    // Check if next button should be disabled
    const isNextDisabled = !hasProAccess && selectedYear === 2026 && selectedMonth >= 4;

    if (!token) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Card className="max-w-md">
                    <CardContent className="pt-6 text-center">
                        <p className="text-muted-foreground">Please login to view your personal analysis.</p>
                        <Button className="mt-4" onClick={() => window.location.href = '/login'}>
                            Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center" data-testid="analysis-loading">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const summary = analysisData?.summary || {};
    const userInfo = analysisData?.user_info;

    // Helper function to render day cards with locking for non-PRO users
    const renderDayCards = (days, type) => {
        if (!days || days.length === 0) {
            return <p className="text-muted-foreground text-sm text-center py-4">No days found</p>;
        }

        return days.map((day, index) => (
            <DayCard 
                key={day.day} 
                day={day} 
                type={type} 
                isLocked={!hasProAccess && index >= FREE_VISIBLE_DATES}
            />
        ));
    };

    return (
        <div className="min-h-screen bg-background" data-testid="personal-analysis-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-serif text-2xl sm:text-3xl font-bold">Personal Date Analysis</h1>
                        <p className="text-muted-foreground mt-1">Auspicious & inauspicious days based on your BaZi</p>
                    </div>
                    
                    {/* Month Navigation */}
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" onClick={handlePrevMonth} data-testid="prev-month-btn">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <div className={`px-4 py-2 bg-muted rounded-lg min-w-[140px] text-center ${isMonthLocked ? 'opacity-60' : ''}`}>
                            <span className="font-medium">{format(new Date(selectedYear, selectedMonth - 1), 'MMMM yyyy')}</span>
                            {isMonthLocked && <Lock className="w-3 h-3 inline-block ml-2 text-slate-500" />}
                        </div>
                        <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={handleNextMonth} 
                            disabled={isNextDisabled}
                            data-testid="next-month-btn"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Locked Month Message */}
                {isMonthLocked && (
                    <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-3">
                        <Lock className="w-5 h-5 text-amber-600" />
                        <div>
                            <p className="font-medium text-amber-800 dark:text-amber-200">PRO Access Required</p>
                            <p className="text-sm text-amber-600 dark:text-amber-400">Upgrade to PRO to view analysis for April 2026 onwards</p>
                        </div>
                    </div>
                )}

                {/* Summary Cards */}
                <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 ${isMonthLocked ? 'opacity-40 pointer-events-none' : ''}`}>
                    <Card className="border-emerald-200 dark:border-emerald-800" data-testid="best-day-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2 text-emerald-600">
                                <Star className="w-4 h-4" /> Best Day
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {summary.most_auspicious_day ? (
                                <>
                                    <div className="text-2xl font-bold">{format(new Date(summary.most_auspicious_day.date), 'MMM d')}</div>
                                    <div className="text-sm text-muted-foreground">{summary.most_auspicious_day.day_animal}</div>
                                    <Badge className="mt-2 bg-emerald-100 text-emerald-800">Score: +{summary.most_auspicious_day.net_score}</Badge>
                                </>
                            ) : <div className="text-muted-foreground">No data</div>}
                        </CardContent>
                    </Card>

                    <Card className="border-rose-200 dark:border-rose-800" data-testid="worst-day-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2 text-rose-600">
                                <AlertTriangle className="w-4 h-4" /> Worst Day
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {summary.most_inauspicious_day ? (
                                <>
                                    <div className="text-2xl font-bold">{format(new Date(summary.most_inauspicious_day.date), 'MMM d')}</div>
                                    <div className="text-sm text-muted-foreground">{summary.most_inauspicious_day.day_animal}</div>
                                    <Badge className="mt-2 bg-rose-100 text-rose-800">Score: {summary.most_inauspicious_day.net_score}</Badge>
                                </>
                            ) : <div className="text-muted-foreground">No data</div>}
                        </CardContent>
                    </Card>

                    <Card className="border-amber-200 dark:border-amber-800" data-testid="nobleman-count-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
                                <Crown className="w-4 h-4" /> Nobleman Days
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{summary.total_nobleman_days || 0}</div>
                            <div className="text-sm text-muted-foreground">Days with 貴人 support</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Day Lists */}
                <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 relative ${isMonthLocked ? 'opacity-40 pointer-events-none' : ''}`}>
                    {/* Auspicious Days */}
                    <Card data-testid="auspicious-days-section">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Star className="w-5 h-5 text-emerald-600" />
                                Auspicious Days
                            </CardTitle>
                            <CardDescription>{summary.total_auspicious_days || 0} days found</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
                            {renderDayCards(analysisData?.auspicious_days, 'auspicious')}
                        </CardContent>
                    </Card>

                    {/* Nobleman Days */}
                    <Card data-testid="nobleman-days-section">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Crown className="w-5 h-5 text-amber-600" />
                                Nobleman Days 貴人
                            </CardTitle>
                            <CardDescription>Days with noble support</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
                            {renderDayCards(analysisData?.nobleman_days, 'nobleman')}
                        </CardContent>
                    </Card>

                    {/* Inauspicious Days */}
                    <Card data-testid="inauspicious-days-section">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-rose-600" />
                                Inauspicious Days
                            </CardTitle>
                            <CardDescription>{summary.total_inauspicious_days || 0} days to be cautious</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
                            {renderDayCards(analysisData?.inauspicious_days, 'inauspicious')}
                        </CardContent>
                    </Card>
                </div>

                {/* Upgrade CTA for non-PRO users */}
                {!hasProAccess && (
                    <div className="mt-8 text-center p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                        <Lock className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200">Unlock Full Analysis</h3>
                        <p className="text-sm text-amber-600 dark:text-amber-400 mt-1 mb-4">
                            Get access to all dates, future months, and detailed insights
                        </p>
                        <a href="/subscription">
                            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                                Upgrade to PRO - $7 Trial
                            </Button>
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PersonalAnalysis;
